/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridRowParams,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useQuery } from "@tanstack/react-query";
import { getOtherGraphFavoritesQuery } from "../../../lib/Queries";
import { OtherGraphFavoriteData, SiteMetadata } from "../../../lib/Types";
import TimeSeriesLineGraphModal from "./TimeSeriesLineGraphModal";

interface Props {
  site: SiteMetadata;
  isOpen: boolean;
  onClose: () => void;
  onBookmarked?: () => void;
}

interface OtherGraphRow {
  id: string;
  userName: string;
  title: string;
  graph: OtherGraphFavoriteData;
}

type OtherGraphFilterOptions = {
  userName: Set<string>;
  title: Set<string>;
};

const styles = {
  modalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "60%",
    maxHeight: "90%",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "5px",
    padding: "2rem",
    minHeight: "150px",
    overflow: "auto",
  },
  datagrid: {
    border: "none",
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: "none",
    },
    "& .MuiDataGrid-cell": {
      borderBottom: "none",
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-row": {
      borderBottom: "none",
      cursor: "pointer",
    },
    "& .MuiDataGrid-footerContainer": {
      border: "none",
    },
  },
};

export default function OtherGraphsModal({ site, isOpen, onClose, onBookmarked }: Props) {
  const [appliedFilter, setAppliedFilter] = useState<OtherGraphFilterOptions>({
    userName: new Set<string>(),
    title: new Set<string>(),
  });
  const [selectedGraph, setSelectedGraph] = useState<OtherGraphFavoriteData | null>(null);

  const { data: otherGraphs } = useQuery({
    ...getOtherGraphFavoritesQuery(site.uuid),
    enabled: isOpen,
  });

  const rows: OtherGraphRow[] = useMemo(() => {
    if (!otherGraphs) return [];
    return otherGraphs.map((graph) => ({
      id: graph.uuid,
      userName: graph.user_name || "Unknown User",
      title: graph.title,
      graph,
    }));
  }, [otherGraphs]);

  const getFilteredRows = (allRows: OtherGraphRow[], filter: OtherGraphFilterOptions) => {
    return allRows.filter((row) => {
      const userMatches = filter.userName.size === 0 || filter.userName.has(row.userName);
      const titleMatches = filter.title.size === 0 || filter.title.has(row.title);
      return userMatches && titleMatches;
    });
  };

  const filteredRows = useMemo(() => getFilteredRows(rows, appliedFilter), [rows, appliedFilter]);

  /** Options for one filter reflect the rows left after the other filter is applied. */
  const getFilterOptions = (field: keyof OtherGraphFilterOptions) => {
    const otherFieldFilter: OtherGraphFilterOptions = {
      userName: new Set(appliedFilter.userName),
      title: new Set(appliedFilter.title),
    };
    otherFieldFilter[field] = new Set<string>();
    const options = new Set(getFilteredRows(rows, otherFieldFilter).map((row) => row[field]));
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  };

  const handleFilterChange =
    (field: keyof OtherGraphFilterOptions) => (_: any, value: string[]) => {
      setAppliedFilter((prev) => {
        const next: OtherGraphFilterOptions = {
          userName: new Set(prev.userName),
          title: new Set(prev.title),
        };
        next[field] = new Set(value);
        return next;
      });
    };

  const getSelector = (label: string, field: keyof OtherGraphFilterOptions) => (
    <Autocomplete
      multiple
      options={getFilterOptions(field)}
      value={Array.from(appliedFilter[field])}
      onChange={handleFilterChange(field)}
      renderInput={(params) => <TextField {...params} label={label} size="small" />}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
        ))
      }
      sx={{ minWidth: 220 }}
    />
  );

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <Stack direction="row" spacing={2} alignItems="start" sx={{ width: "100%", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
          {getSelector("User Name", "userName")}
          {getSelector("Saved Graph Name", "title")}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <GridToolbarQuickFilter placeholder="Search all" />
        </Box>
      </Stack>
    </GridToolbarContainer>
  );

  const columns: GridColDef[] = [
    { field: "userName", headerName: "User Name", flex: 1 },
    { field: "title", headerName: "Saved Graph Name", flex: 1 },
  ];

  const handleRowClick = (params: GridRowParams) => {
    setSelectedGraph((params.row as OtherGraphRow).graph);
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose} aria-labelledby="other-graphs-modal-title">
        <Box sx={styles.modalBox}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "1.5rem",
            }}
          >
            <Typography id="other-graphs-modal-title" variant="body2" color="secondary">
              Other Graphs
            </Typography>
            <IconButton onClick={onClose}>
              <CloseOutlinedIcon fontSize="inherit" sx={{ fontSize: "24px" }} />
            </IconButton>
          </Box>
          <Box sx={{ width: "100%", height: 500 }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              onRowClick={handleRowClick}
              density="compact"
              hideFooterRowCount={true}
              disableRowSelectionOnClick
              slots={{ toolbar: CustomToolbar }}
              slotProps={{ toolbar: { showQuickFilter: true } }}
              sx={styles.datagrid}
            />
          </Box>
        </Box>
      </Modal>
      {selectedGraph && (
        <TimeSeriesLineGraphModal
          site={site}
          isOpen={Boolean(selectedGraph)}
          onClose={() => setSelectedGraph(null)}
          graphFavorite={selectedGraph}
          isOtherUsersGraph={true}
          onBookmarked={onBookmarked}
        />
      )}
    </>
  );
}
